import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericSelect } from "@shared/components/Select";
import React, { useEffect, useMemo, useState } from "react";
import { IconButton } from "@shared/components/IconButton";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  usePutAdjustReadings,
  usePutPassExemptReading,
  useValidationCodes,
} from "../../hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { useValidationContext } from "../../context";
import { toast } from "@shared/hooks/use-toast";
import { logEvent } from "@shared/analytics/analytics";

interface ExemptReadingDialogProps {
  open: boolean;
  onClose: () => void;
  onExempt: (reasonCode: string) => void;
}
export const ExemptReadingDialog: React.FC<ExemptReadingDialogProps> = ({
  open,
  onClose,
  onExempt,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const remoteUtilityId = useRemoteUtilityId();
  const { readingId, meterRunId, goToNextReading } = useValidationContext();
  const { data: validationCodes } = useValidationCodes(remoteUtilityId);
  const exemptReading = usePutPassExemptReading(readingId, meterRunId);

  // const codeList = mapToLabelValue(validationCodes, "name", "id");
  const codeList = useMemo(
    () =>
      validationCodes?.map((item: any) => ({
        label: item.code + " - " + item.name,
        value: item.id,
      })) || [],
    [validationCodes]
  );

  const handleExempt = () => {
    if (selectedReason) {
      exemptReading.mutate(
        {
          validation_code: selectedReason,
          rule_status: 2,
        },
        {
          onSuccess: () => {
            logEvent("MX Reading Exempted", {
              reason: selectedReason,
            });
            toast({
              title: "Success",
              description: "Meter exempted successfully.",
              variant: "default", // or leave out since "default" is implicit
            });
            goToNextReading();
            setSelectedReason("");
            onClose();
          },
        }
      );
    }
  };

  const handleClose = () => {
    setSelectedReason("");
    onClose();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md rounded-xl p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Exempt Reading
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Select a reason code for exempting this reading.
          </p>
        </DialogHeader>

        <div className="mt-4">
          <label
            htmlFor="reasonCode"
            className="text-sm font-medium block mb-1"
          >
            Reason Code
          </label>
          <GenericSelect
            placeholder="Select a reason code"
            options={codeList}
            value={selectedReason}
            onValueChange={(val: string) => setSelectedReason(val)}
          />
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-3">
          <DialogClose asChild>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </DialogClose>
          <IconButton
            label="Exempt"
            disabled={!selectedReason}
            onClick={handleExempt}
            className="bg-yellow-400 text-black hover:bg-yellow-400"
          ></IconButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
